import {logUtishaDebug, logUtishaInfo, logUtishaWarn} from 'src/app/util/log';
import {SessionBreadcrumb} from './session-breadcrumbs';

/**
 * Callbacks the guardian uses to react to browser lifecycle events. Like
 * MediaWatchdog, it intentionally holds no Stream.io or Angular references —
 * the host wires concrete implementations in and the guardian just fires
 * them at the right moments.
 */
export interface SessionLifecycleHooks {
  /**
   * Force an immediate media health check. Called on visibility return,
   * BFCache restore, online transition, and permission change. The host
   * implementation should delegate to MediaWatchdog.checkNow().
   */
  forceHealthCheck: () => void;
  /**
   * Called when the browser asks about permission for camera/microphone
   * (e.g. the user revoked in site settings while a session is active).
   * The host decides whether to surface a UI.
   */
  onPermissionChanged?: (kind: 'camera' | 'microphone', state: PermissionState) => void;
  /**
   * Called when the device list changes and the currently-selected
   * camera/mic is no longer present. The host should fall back to another
   * device.
   */
  onCurrentDeviceVanished?: (kind: 'camera' | 'microphone') => void;
  /** Currently selected device ids, for the 'vanished' check above. */
  getCurrentCameraId?: () => string | undefined;
  getCurrentMicrophoneId?: () => string | undefined;
}

/**
 * SessionLifecycleGuardian wires up the browser lifecycle signals that
 * *commonly* interrupt an active media session but are *not* surfaced
 * through the Stream SDK:
 *
 *  - visibilitychange → tab backgrounding; mobile browsers throttle or
 *    suspend tracks. On return, the canvas loop may be stalled and the
 *    underlying camera track may be dead even though the SDK still
 *    reports it as publishing.
 *  - pageshow with event.persisted → BFCache restore; the DOM is the old
 *    DOM but all network/media state is gone.
 *  - freeze/resume → Chrome's freeze policy equivalent of BFCache.
 *  - online → network came back; always verify.
 *  - devicechange → USB/bluetooth device churn; if the currently-selected
 *    camera/mic is gone, the host must switch.
 *  - permissions.onchange → user revoked camera/mic in site settings
 *    during an active session.
 *  - Wake Lock API (navigator.wakeLock.request('screen')) → request
 *    during the session so screen sleep doesn't kill the camera on
 *    mobile.
 *
 * All of these are cheap event listeners; the heavy lifting happens in
 * the hooks the host provides. Start in StreamSessionMedia.initialize()
 * after the watchdog, stop in destroy() before the watchdog.
 */
export class SessionLifecycleGuardian {
  private started = false;
  private readonly handlers: Array<() => void> = [];
  private cameraPermission?: PermissionStatus;
  private microphonePermission?: PermissionStatus;
  private wakeLock: any = null; // WakeLockSentinel; typed loosely for broad browser support

  constructor(private readonly hooks: SessionLifecycleHooks) {}

  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    logUtishaInfo('SessionLifecycleGuardian: starting');

    this.bindDocumentEvent('visibilitychange', () => this.onVisibilityChange());
    this.bindWindowEvent('pageshow', (e: any) => this.onPageShow(e));
    this.bindWindowEvent('pagehide', () => logUtishaDebug('SessionLifecycleGuardian: pagehide'));
    this.bindDocumentEvent('freeze', () => logUtishaDebug('SessionLifecycleGuardian: document frozen'));
    this.bindDocumentEvent('resume', () => this.onResume());
    this.bindWindowEvent('online', () => this.onOnline());
    this.bindWindowEvent('offline', () => logUtishaWarn(SessionBreadcrumb.LIFECYCLE_OFFLINE));

    if (navigator.mediaDevices && typeof navigator.mediaDevices.addEventListener === 'function') {
      const handler = () => this.onDeviceChange();
      navigator.mediaDevices.addEventListener('devicechange', handler);
      this.handlers.push(() => navigator.mediaDevices.removeEventListener('devicechange', handler));
    }

    await this.subscribePermissions();
    await this.requestWakeLock();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    logUtishaDebug('SessionLifecycleGuardian: stopping');
    for (const off of this.handlers) {
      try { off(); } catch (_) { /* best-effort */ }
    }
    this.handlers.length = 0;
    if (this.cameraPermission) {
      this.cameraPermission.onchange = null;
      this.cameraPermission = undefined;
    }
    if (this.microphonePermission) {
      this.microphonePermission.onchange = null;
      this.microphonePermission = undefined;
    }
    this.releaseWakeLock();
  }

  private bindDocumentEvent(event: string, handler: (e: Event) => void): void {
    document.addEventListener(event, handler);
    this.handlers.push(() => document.removeEventListener(event, handler));
  }

  private bindWindowEvent(event: string, handler: (e: Event) => void): void {
    window.addEventListener(event, handler);
    this.handlers.push(() => window.removeEventListener(event, handler));
  }

  private onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      logUtishaInfo('SessionLifecycleGuardian: tab visible — forcing health check');
      this.hooks.forceHealthCheck();
      // Re-acquire a wake lock; the previous one is released when the tab
      // is hidden on most browsers.
      this.requestWakeLock().catch(() => {});
    } else {
      logUtishaDebug('SessionLifecycleGuardian: tab hidden');
    }
  }

  private onPageShow(e: PageTransitionEvent): void {
    if (e.persisted) {
      // BFCache restore — the old Call object is dead. The cheapest safe
      // response is to force a full health check; if tracks are dead the
      // watchdog will fire a republish.
      logUtishaWarn(`${SessionBreadcrumb.LIFECYCLE_BFCACHE_RESTORE} — forcing health check`);
      this.hooks.forceHealthCheck();
    }
  }

  private onResume(): void {
    logUtishaInfo('SessionLifecycleGuardian: document resumed — forcing health check');
    this.hooks.forceHealthCheck();
  }

  private onOnline(): void {
    logUtishaInfo('SessionLifecycleGuardian: online — forcing health check');
    this.hooks.forceHealthCheck();
  }

  private onDeviceChange(): void {
    logUtishaDebug('SessionLifecycleGuardian: devicechange');
    // Give the browser a moment to finish updating the enumeration —
    // enumerateDevices immediately after the event can still show the
    // old list on some browsers.
    setTimeout(() => {
      this.checkCurrentDevicesStillPresent().catch(e =>
        logUtishaWarn('SessionLifecycleGuardian: device enumeration failed after devicechange: ' + (e?.message || e))
      );
    }, 150);
  }

  private async checkCurrentDevicesStillPresent(): Promise<void> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameraId = this.hooks.getCurrentCameraId?.();
    const micId = this.hooks.getCurrentMicrophoneId?.();

    if (cameraId) {
      const hasCamera = devices.some(d => d.kind === 'videoinput' && d.deviceId === cameraId);
      if (!hasCamera) {
        logUtishaWarn(`${SessionBreadcrumb.LIFECYCLE_CAMERA_GONE} ${cameraId.slice(0, 8)} is gone`);
        this.hooks.onCurrentDeviceVanished?.('camera');
      }
    }
    if (micId) {
      const hasMic = devices.some(d => d.kind === 'audioinput' && d.deviceId === micId);
      if (!hasMic) {
        logUtishaWarn(`${SessionBreadcrumb.LIFECYCLE_MIC_GONE} ${micId.slice(0, 8)} is gone`);
        this.hooks.onCurrentDeviceVanished?.('microphone');
      }
    }
  }

  private async subscribePermissions(): Promise<void> {
    // Permissions API is gated by a feature check — Safari doesn't expose
    // camera/microphone as permission names on all versions.
    if (!('permissions' in navigator) || typeof navigator.permissions.query !== 'function') {
      return;
    }
    try {
      this.cameraPermission = await navigator.permissions.query({name: 'camera' as PermissionName});
      this.cameraPermission.onchange = () => {
        const state = this.cameraPermission!.state;
        logUtishaWarn(`${SessionBreadcrumb.LIFECYCLE_CAMERA_PERMISSION} ${state}`);
        this.hooks.onPermissionChanged?.('camera', state);
      };
    } catch (_) { /* unsupported — silently skip */ }
    try {
      this.microphonePermission = await navigator.permissions.query({name: 'microphone' as PermissionName});
      this.microphonePermission.onchange = () => {
        const state = this.microphonePermission!.state;
        logUtishaWarn(`${SessionBreadcrumb.LIFECYCLE_MIC_PERMISSION} ${state}`);
        this.hooks.onPermissionChanged?.('microphone', state);
      };
    } catch (_) { /* unsupported — silently skip */ }
  }

  private async requestWakeLock(): Promise<void> {
    // Wake Lock API prevents the screen from sleeping; on mobile, screen
    // sleep typically kills the active camera track. Best-effort — no
    // degradation if unsupported or denied.
    const anyNav = navigator as any;
    if (!anyNav.wakeLock || typeof anyNav.wakeLock.request !== 'function') return;
    if (this.wakeLock) return;
    try {
      this.wakeLock = await anyNav.wakeLock.request('screen');
      logUtishaDebug('SessionLifecycleGuardian: screen wake lock acquired');
      this.wakeLock.addEventListener?.('release', () => {
        logUtishaDebug('SessionLifecycleGuardian: wake lock released');
        this.wakeLock = null;
      });
    } catch (e: any) {
      logUtishaDebug('SessionLifecycleGuardian: wake lock request failed: ' + (e?.message || e));
    }
  }

  private releaseWakeLock(): void {
    if (this.wakeLock) {
      try { this.wakeLock.release?.(); } catch (_) { /* best-effort */ }
      this.wakeLock = null;
    }
  }
}
